import React, { useEffect } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { createSignalRContext } from "react-signalr";
import Overview from "views/Overview";
import Login from "views/Login";
import Redirect from "views/Redirect";
import PrivateRoute from "components/PrivateRoute";
import Equity from "views/Equity";
import Art from "views/Art";
import Forest from "views/Forest";
import MixedAllocation from "views/MixedAllocation";
import Commodity from "views/Commodity";
import Derivatives from "views/Derivatives";
import RealEstate from "views/RealEstate";
import FixedIncome from "views/FixedIncome";
import PrivateDebt from "views/PrivateDebt";
import PrivateEquity from "views/PrivateEquity";
import Structured from "views/Structured";
import Logs from "views/Logs";
import Statements from "views/Statements";
import ProfitsAndLosses from "views/ProfitsAndLosses";
import Covenants from "views/Covenants";
import Logout from "views/Logout";
import Currency from "views/Currency";
import DirectInvestments from "views/DirectInvestments";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { removeState } from "redux/oidc/oidcFunctions";
import { RootState } from "redux/rootReducer";
import Specialty from "views/Specialty";
import Crypto from "views/Crypto";

export const SignalRContext = createSignalRContext({
  shareConnectionBetweenTab: false,
});

export enum Routes {
  Art = "/art",
  Forest = "/forest",
  Derivatives = "/derivatives",
  Login = "/login",
  Logout = "/logout",
  Redirect = "/redirect",
  Overview = "/",
  Commodity = "/commodity",
  Equity = "/equity",
  FixedIncome = "/fixedIncome",
  MixedAllocation = "/mixedAllocation",
  PrivateDebt = "/privateDebt",
  PrivateEquity = "/privateEquity",
  RealEstate = "/realEstate",
  Structured = "/structured",
  Logs = "/logs",
  Specialty = "/specialty",
  Statements = "/statements",
  ProfitsAndLosses = "/profitsAndLosses",
  Covenants = "/covenants",
  Error = "/error/:errorType",
  Currency = "/currency",
  DirectInvestments = "/directInvestments",
  Crypto = "/crypto",
}

function App() {
  const dispatch = useDispatch();

  const token = useSelector((state: RootState) => state.oidc.data?.AccessToken, shallowEqual);

  useEffect(() => {
    if (Boolean(token)) {
      const timer = window.setTimeout(() => {
        dispatch(removeState());
      }, 10800000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [token, dispatch]);

  return (
    <SignalRContext.Provider
      connectEnabled={!!token}
      accessTokenFactory={() => token ?? ""}
      dependencies={[token]} //remove previous connection and create a new connection if changed
      url={`${process.env.REACT_APP_API_URL}/api/reporting/hub`}
    >
      <Router>
        <Switch>
          <Route path={Routes.Login}>
            <Login />
          </Route>
          <Route path={Routes.Logout}>
            <Logout />
          </Route>
          <Route path={Routes.Error}>
            <Login />
          </Route>
          <Route path={Routes.Redirect}>
            <Redirect />
          </Route>
          <PrivateRoute path={Routes.Art} component={Art} />
          <PrivateRoute path={Routes.Commodity} component={Commodity} />
          <PrivateRoute path={Routes.Currency} component={Currency} />
          <PrivateRoute path={Routes.Derivatives} component={Derivatives} />
          <PrivateRoute path={Routes.DirectInvestments} component={DirectInvestments} />
          <PrivateRoute path={Routes.Equity} component={Equity} />
          <PrivateRoute path={Routes.FixedIncome} component={FixedIncome} />
          <PrivateRoute path={Routes.Forest} component={Forest} />
          <PrivateRoute path={Routes.MixedAllocation} component={MixedAllocation} />
          <PrivateRoute path={Routes.PrivateDebt} component={PrivateDebt} />
          <PrivateRoute path={Routes.PrivateEquity} component={PrivateEquity} />
          <PrivateRoute path={Routes.RealEstate} component={RealEstate} />
          <PrivateRoute path={Routes.Structured} component={Structured} />
          <PrivateRoute path={Routes.ProfitsAndLosses} component={ProfitsAndLosses} />
          <PrivateRoute path={Routes.Logs} component={Logs} />
          <PrivateRoute path={Routes.Statements} component={Statements} />
          <PrivateRoute path={Routes.Covenants} component={Covenants} />
          <PrivateRoute path={Routes.Specialty} component={Specialty} />
          <PrivateRoute path={Routes.Crypto} component={Crypto} />
          {/* This needs to be last in order to render the other pages */}
          <PrivateRoute path={Routes.Overview} component={Overview} />
        </Switch>
      </Router>
    </SignalRContext.Provider>
  );
}

export default App;
